import { useSymbolSelectorWidth } from '@src/components/SymbolSelector/useSymbolSelectorWidth';
import {
  Box,
  Divider,
  Flex,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  InlineFormattedNumber,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  SymbolSelectorAppearance,
  Text,
  Toggle,
  VStack,
  canViewDeepDive,
  daysUntil,
  isFuture,
  isMultileg,
  isOption,
  isPerpetualSwap,
  isSpot,
  useMarketDataStatistics,
  useMixpanel,
  useObservableValue,
  useRefRate,
  useSecuritiesContext,
  useSparklineData,
  type MarketDataStatistics,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import { useCallback, useMemo } from 'react';
import { timedRate } from '../../../../components/ContractDetails';
import type { MultilegComboType } from '../../../../components/MultilegCombo/enums';
import { SymbolActionsMenu } from '../../../../components/SymbolActionsMenu';
import { SymbolSelector } from '../../../../components/SymbolSelector';
import { useBuyingPower, useFeatureFlag } from '../../../../hooks';
import { useNavigateToMultilegCombo } from '../../../../hooks/useNavigateToMultilegCombo';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';
import { useTradingSettings } from '../../../../providers/TradingSettingsContext';
import { NEXT_FUNDING_TIME, PREDICTED_NEXT_FUNDING_RATE } from '../../../../tokens/marketDataStats';
import { AddMarket } from '../Markets/AddMarket';
import { MarketsList } from '../Markets/MarketsList';
import { BuyingPowerSummary } from './BuyingPowerSummary';
import { DeepDiveMarketSelector } from './DeepDiveMarketSelector';
import { MarketSummary } from './MarketSummary';
import { SparklineSummary } from './SparklineSummary';

interface DeepDiveHeaderParams {
  security: Security;
  exchangeMarkets: string[];
}

export const DeepDiveHeader = ({ security, exchangeMarkets }: DeepDiveHeaderParams) => {
  const mixpanel = useMixpanel();
  const { searchableSecurities } = useSecuritiesContext();
  const { useNewMarketSelector } = useTradingSettings();
  const showLatestRefRate = isSpot(security);
  const showPercentageChange = isSpot(security);
  const showUnderlyingIndex = isOption(security);
  const showPerpsStats = isPerpetualSwap(security);
  const showFuturesStats = isFuture(security);
  const showOptionsStats = isOption(security);
  const showMarketSummary = isPerpetualSwap(security) || isFuture(security) || isOption(security);
  const { canViewCreditAndBalances } = useFeatureFlag();

  const {
    canChangeMarkets,
    selectedMarketAccounts,
    selectedMarkets,
    showMarkets,
    setShowMarkets,
    setSymbol,
    closeDeepDive,
    selectedExchangeMarkets,
  } = useDeepDiveContext();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();

  const handleCreateMultileg = useCallback(
    (multilegType: MultilegComboType) => {
      mixpanel.track(MixpanelEvent.CreateNewMultileg, {
        [MixpanelEventProperty.Type]: multilegType,
        [MixpanelEventProperty.Source]: MixpanelEventSource.DeepDive,
      });
      navigateToMultilegCombo(multilegType, undefined, selectedMarketAccounts, selectedMarkets, [security.Symbol]);
    },
    [mixpanel, navigateToMultilegCombo, security.Symbol, selectedMarketAccounts, selectedMarkets]
  );
  const { marketDataStatistics } = useMarketDataStatistics(security.Symbol, selectedMarkets, 'DEEP_DIVE');

  const handleChangeMarkets = useCallback(() => {
    mixpanel.track(MixpanelEvent.ToggleMarkets);
    setShowMarkets(!showMarkets);
  }, [mixpanel, setShowMarkets, showMarkets]);

  const handleChangeSymbol = useCallback(
    (symbol?: string) => {
      if (symbol) {
        mixpanel.track(MixpanelEvent.ChangeSymbol);
        setSymbol(symbol);
      }
    },
    [mixpanel, setSymbol]
  );

  const availableSecurities = useMemo(
    // There's a different deepDive for multilegs
    () => searchableSecurities?.filter(s => canViewDeepDive(s) && !isMultileg(s)),
    [searchableSecurities]
  );

  const dropdownWidth = useSymbolSelectorWidth();

  const { buyingPowerByMarketAccount: baseBuyingPower } = useBuyingPower({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    tag: 'DeepDiveHeader',
    enabled: canViewCreditAndBalances,
  });

  return (
    <VStack w="100%" mb="spacing2">
      <HStack background="backgroundContent" justifyContent="flex-start" gap="spacing8" w="100%" p="spacing8">
        <SymbolSelector
          appearance={SymbolSelectorAppearance.Ghost}
          symbol={security.Symbol}
          availableSecurities={availableSecurities}
          onSymbolChanged={handleChangeSymbol}
          startOpen={false}
          dropdownWidth={dropdownWidth}
          maxHeight={480}
          showGroups={true}
          size={FormControlSizes.Small}
          showFavorites={true}
          dropdownId="deep-dive-symbol-selector-dropdown"
          id="deep-dive-symbol-selector"
        />
        {showLatestRefRate && <LatestRefRate security={security} />}
        {showPercentageChange && <PercentageChange security={security} />}
        {showUnderlyingIndex && <UnderlyingIndex security={security} marketDataStatistics={marketDataStatistics} />}
        {exchangeMarkets.length > 0 && <SparklineSummary security={security} exchangeMarkets={exchangeMarkets} />}
        <HStack gap="spacing8" flex="auto" justifyContent="flex-end">
          {canChangeMarkets && (
            <>
              <Toggle
                size={FormControlSizes.Small}
                label="Markets"
                checked={Boolean(showMarkets)}
                onChange={handleChangeMarkets}
                tooltip="Show the markets selector panel"
              />
              <Divider orientation="vertical" my="spacing12" />
              {canViewCreditAndBalances && <BuyingPowerSummary baseBuyingPower={baseBuyingPower} security={security} />}
            </>
          )}
          {showPerpsStats && <PerpsStats security={security} marketDataStatistics={marketDataStatistics} />}
          {showFuturesStats && <FuturesStats security={security} marketDataStatistics={marketDataStatistics} />}
          {showOptionsStats && <OptionsStats marketDataStatistics={marketDataStatistics} />}
          <SymbolActionsMenu
            security={security}
            mixpanelSource={MixpanelEventSource.DeepDive}
            onCreateMultileg={handleCreateMultileg}
            dataTestId="deep-dive-menu"
          />
          <IconButton ghost color="colors.gray.090" icon={IconName.Close} onClick={closeDeepDive} />
        </HStack>
      </HStack>
      {canChangeMarkets && (
        <Flex background="colors.gray.020" p="spacing8" justifyContent="flex-start" w="100%" gap="spacing8">
          {useNewMarketSelector ? (
            <HStack gap="spacing8" justifyContent="flex-start">
              <DeepDiveMarketSelector security={security} />
              <Divider orientation="vertical" />
              {selectedExchangeMarkets.length === 0 && (
                <Box minWidth={300} px="spacing8" mr="auto" flex="1" whiteSpace="nowrap">
                  <Text>With no exchange selected, the DeepDive is displaying OHLCV data for all exchanges.</Text>
                </Box>
              )}
            </HStack>
          ) : null}
          <MarketsList baseBuyingPower={baseBuyingPower} security={security}>
            {!useNewMarketSelector && <AddMarket security={security} />}
          </MarketsList>
        </Flex>
      )}
      {showMarketSummary && (
        <Flex background="colors.gray.020" p="spacing8" justifyContent="flex-start" w="100%" gap="spacing8">
          <MarketSummary security={security} />
        </Flex>
      )}
    </VStack>
  );
};

// only for Spot
function LatestRefRate({ security }: { security: Security }) {
  const refRatesObservable = useRefRate(security.Symbol);
  const latestRefRate = useObservableValue(() => refRatesObservable, [refRatesObservable]);

  return (
    <Text weight="fontWeightMedium" data-testid="current-price">
      <InlineFormattedNumber
        number={latestRefRate?.currentPrice || 0}
        specification={security.PriceDisplaySpec}
        increment={security.DefaultPriceIncrement}
      />
    </Text>
  );
}

// only for Spot
function PercentageChange({ security }: { security: Security }) {
  const sparklinesObservable = useSparklineData(security.Symbol);
  const latestSparkline = useObservableValue(() => sparklinesObservable, [sparklinesObservable]);
  const percentChange = Big(latestSparkline?.PercentChange ?? 0);

  return (
    <Text
      weight="fontWeightMedium"
      color={percentChange.gt(0) ? 'colorTextPositive' : percentChange.lt(0) ? 'colorTextNegative' : undefined}
      data-testid="percentage-change"
    >
      {percentChange.gt(0) ? '+' : ''}
      {percentChange.toFixed(2)}%
    </Text>
  );
}

// only for Options
function UnderlyingIndex({
  security,
  marketDataStatistics,
}: {
  security: Security;
  marketDataStatistics: MarketDataStatistics | undefined;
}) {
  if (!marketDataStatistics || !marketDataStatistics.IndexPrice) {
    return null;
  }

  return (
    <VStack alignItems="start" ml="spacing24">
      <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
        Underlying Index
      </Text>
      <Text weight="fontWeightMedium" data-testid="underlying-index">
        <InlineFormattedNumber number={marketDataStatistics.IndexPrice} currency={security.UnderlyingQuoteCurrency} />
      </Text>
    </VStack>
  );
}

// only for Perps
function PerpsStats({
  security,
  marketDataStatistics,
}: {
  security: Security;
  marketDataStatistics: MarketDataStatistics | undefined;
}) {
  if (!marketDataStatistics) {
    return null;
  }

  return (
    <>
      <CommonPerpsAndFuturesStats security={security} marketDataStatistics={marketDataStatistics} />
      <VStack alignItems="start" ml="spacing24">
        <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
          Predicted Next Funding Rate
        </Text>
        <Text weight="fontWeightMedium" data-testid="predicted-next-funding-rate">
          {timedRate(marketDataStatistics, PREDICTED_NEXT_FUNDING_RATE, NEXT_FUNDING_TIME)}
        </Text>
      </VStack>
    </>
  );
}

// only for Futures
function FuturesStats({
  security,
  marketDataStatistics,
}: {
  security: Security;
  marketDataStatistics: MarketDataStatistics | undefined;
}) {
  if (!marketDataStatistics) {
    return null;
  }

  const timeToDelivery = security.Expiration ? daysUntil(new Date(security.Expiration)) : null;

  return (
    <>
      <CommonPerpsAndFuturesStats security={security} marketDataStatistics={marketDataStatistics} />
      <VStack alignItems="start" ml="spacing24">
        <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
          Time to Delivery
        </Text>
        <Text weight="fontWeightMedium" data-testid="time-to-delivery">
          {timeToDelivery}
        </Text>
      </VStack>
    </>
  );
}

const OPTIONS_GREEKS = ['Delta', 'Gamma', 'Vega', 'Theta', 'Rho'] as const;
// only for Options
function OptionsStats({ marketDataStatistics }: { marketDataStatistics: MarketDataStatistics | undefined }) {
  if (!marketDataStatistics) {
    return null;
  }

  return (
    <>
      {OPTIONS_GREEKS.map(greek => (
        <VStack alignItems="start" ml="spacing24" key={greek}>
          <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
            {greek}
          </Text>
          <Text weight="fontWeightMedium" data-testid={greek}>
            <InlineFormattedNumber
              number={marketDataStatistics[greek]}
              increment={greek === 'Delta' ? '0.0001' : greek === 'Gamma' ? '0.000001' : '0.01'}
            />
          </Text>
        </VStack>
      ))}
    </>
  );
}

// Common stats for Perps and Futures
function CommonPerpsAndFuturesStats({
  security,
  marketDataStatistics,
}: {
  security: Security;
  marketDataStatistics: MarketDataStatistics | undefined;
}) {
  const { QuoteCurrency, MinPriceIncrement, PositionCurrency, MinSizeIncrement } = security;

  if (!marketDataStatistics) {
    return null;
  }

  return (
    <>
      <VStack alignItems="start" ml="spacing24">
        <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
          Mark Price
        </Text>
        <Text weight="fontWeightMedium" data-testid="mark-price">
          <InlineFormattedNumber
            increment={MinPriceIncrement}
            number={marketDataStatistics.MarkPrice}
            currency={QuoteCurrency}
          />
        </Text>
      </VStack>
      <VStack alignItems="start" ml="spacing24">
        <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
          Open Interest
        </Text>
        <Text weight="fontWeightMedium" data-testid="open-interest">
          <InlineFormattedNumber
            increment={MinSizeIncrement}
            number={marketDataStatistics.OpenInterest}
            currency={PositionCurrency}
          />
        </Text>
      </VStack>
    </>
  );
}
